import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Download, Sparkles, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SearchBar } from "@/components/layout/Navbar";
import { TemplateGrid } from "@/components/TemplateCard";
import { CategoryIcon, Spinner, FormatBadge } from "@/components/common";
import { api, media } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function Section({ eyebrow, title, items, link, testid }) {
  if (!items?.length) return null;
  return (
    <section className="mx-auto max-w-7xl px-4 pt-20 sm:px-6" data-testid={testid}>
      <div className="mb-8 flex items-end justify-between gap-4">
        <div><p className="eyebrow mb-2">{eyebrow}</p><h2 className="text-2xl font-bold sm:text-3xl">{title}</h2></div>
        <Link to={link} className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-primary hover:underline" data-testid={`${testid}-see-all`}>See all <ArrowRight size={15} /></Link>
      </div>
      <TemplateGrid items={items.slice(0, 8)} />
    </section>
  );
}

function Hero({ data }) {
  const covers = data?.featured?.slice(0, 3) || [];
  return (
    <section className="grain relative overflow-hidden border-b border-border">
      <div className="absolute -left-40 top-0 h-[500px] w-[500px] rounded-full bg-primary/20 blur-[120px]" />
      <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-14 sm:px-6 lg:grid-cols-[1.2fr_1fr] lg:py-24">
        <div className="fade-up">
          <p className="eyebrow mb-5 inline-flex items-center gap-2"><Sparkles size={13} className="text-accent" />Create. Sell. Download.</p>
          <h1 className="text-4xl font-extrabold leading-[1.02] sm:text-5xl lg:text-6xl" data-testid="hero-title">
            Premium Graphic Templates. <span className="text-primary">Ready to Edit.</span>
          </h1>
          <p className="mt-5 max-w-lg text-base text-muted-foreground md:text-lg">Discover, buy and sell professional editable designs.</p>
          <div className="mt-8 max-w-xl"><SearchBar big testid="hero-search-input" /></div>
          <div className="mt-6 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>Formats:</span>{["CDR", "PSD", "AI", "CANVA", "PDF"].map((f) => <Link key={f} to={`/explore?format=${f}`}><FormatBadge f={f} /></Link>)}
          </div>
          <div className="mt-10 flex gap-8">
            {[["templates", "Templates"], ["designers", "Designers"], ["downloads", "Downloads"]].map(([k, l]) => (
              <div key={k}><p className="font-display text-2xl font-bold" data-testid={`hero-stat-${k}`}>{data?.stats?.[k] ?? "—"}</p><p className="text-xs text-muted-foreground">{l}</p></div>
            ))}
          </div>
        </div>
        <div className="relative hidden h-[460px] lg:block">
          {covers.map((t, i) => (
            <Link key={t.id} to={`/template/${t.id}`} className="absolute overflow-hidden rounded-2xl border border-border shadow-2xl transition-transform duration-500 hover:z-10 hover:scale-105"
              style={{ width: 230, height: 300, left: [0, 150, 290][i], top: [70, 0, 110][i], transform: `rotate(${[-6, 2, 7][i]}deg)` }}>
              <img src={media(t.cover_image)} alt={t.title} className="h-full w-full object-cover" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function Categories({ cats }) {
  return (
    <section className="mx-auto max-w-7xl px-4 pt-14 sm:px-6">
      <p className="eyebrow mb-4">Browse by category</p>
      <div className="no-scrollbar -mx-4 flex gap-2.5 overflow-x-auto px-4 pb-2 sm:mx-0 sm:flex-wrap sm:px-0">
        {cats.map((c) => (
          <Link key={c.slug} to={`/explore?category=${c.slug}`} data-testid={`category-chip-${c.slug}`}
            className="flex shrink-0 items-center gap-2 rounded-full border border-border bg-card px-4 py-2.5 text-sm transition-colors hover:border-primary hover:text-primary">
            <CategoryIcon name={c.icon} size={15} />{c.name}<span className="font-mono-ui text-[11px] text-muted-foreground">{c.template_count}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function SellerCta() {
  const { user } = useAuth();
  return (
    <section className="mx-auto max-w-7xl px-4 pt-24 sm:px-6">
      <div className="grain relative overflow-hidden rounded-3xl bg-primary p-8 text-white sm:p-14">
        <div className="relative grid gap-8 md:grid-cols-2 md:items-center">
          <div>
            <p className="font-mono-ui text-xs uppercase tracking-[0.2em] text-white/70">For designers</p>
            <h2 className="mt-3 text-3xl font-bold sm:text-4xl">Turn your CorelDRAW files into income.</h2>
            <p className="mt-4 text-white/80">Export a preview, upload your CDR, PSD or AI file, set your price. We store it securely and deliver it only after payment.</p>
            <Button asChild size="lg" className="mt-8 rounded-full bg-white text-primary hover:bg-white/90" data-testid="cta-become-seller-btn">
              <Link to={user?.is_seller ? "/seller/upload" : "/seller"}>{user?.is_seller ? "Sell a Template" : "Become a Seller"} <ArrowRight size={16} className="ml-1" /></Link>
            </Button>
          </div>
          <div className="grid gap-3">
            {[[Upload, "Upload preview + editable file"], [ShieldCheck, "Files stored privately — never public"], [Download, "Buyers download after verified payment"]].map(([I, t]) => (
              <div key={t} className="flex items-center gap-3 rounded-2xl bg-white/10 p-4 backdrop-blur"><I size={20} /><span className="text-sm font-medium">{t}</span></div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [data, setData] = useState(null);
  const [cats, setCats] = useState([]);
  useEffect(() => {
    api.get("/home").then((r) => setData(r.data));
    api.get("/categories").then((r) => setCats(r.data));
  }, []);
  return (
    <div>
      <Hero data={data} />
      <Categories cats={cats} />
      {!data ? <Spinner /> : (
        <>
          <Section eyebrow="Hand-picked" title="Featured templates" items={data.featured} link="/explore" testid="section-featured" />
          <Section eyebrow="Top rated" title="Popular right now" items={data.popular} link="/explore?sort=rating" testid="section-popular" />
          <Section eyebrow="Fresh drops" title="New templates" items={data.newest} link="/explore?sort=newest" testid="section-new" />
          <Section eyebrow="Most downloaded" title="Best-selling" items={data.best_selling} link="/explore?sort=popular" testid="section-best-selling" />
        </>
      )}
      <SellerCta />
    </div>
  );
}
